/**
 * User role management utilities
 * Now using backend API instead of Firestore
 */

import { kyClient } from '@/shared/api';

export type UserRole = 'participant' | 'content_creator' | 'admin';

interface UserProfileResponse {
  id: string;
  email: string;
  role: UserRole;
}

// In-memory cache for user role (cleared on logout)
let roleCache: { uid: string; role: UserRole; timestamp: number } | null = null;
const CACHE_TTL = 1000 * 60 * 5; // 5 minutes

/**
 * Clear the role cache (call on logout)
 */
export function clearRoleCache(): void {
  roleCache = null;
}

/**
 * Get user role from backend API
 * Calls GET /api/users/me to fetch user profile with role
 *
 * Note: uid parameter is kept for backwards compatibility but not used
 * The backend determines the user from the auth token
 */
export async function getUserRole(uid: string): Promise<UserRole> {
  try {
    // Check cache first
    if (roleCache && roleCache.uid === uid) {
      const now = Date.now();
      if (now - roleCache.timestamp < CACHE_TTL) {
        console.log(`[Role] Using cached role for user ${uid}:`, roleCache.role);
        return roleCache.role;
      }
    }

    // Fetch from API
    console.log(`[Role] Fetching role from API for user ${uid}`);
    const response = await kyClient.get('api/users/me').json<UserProfileResponse>();

    const role = response.role || 'participant';
    console.log(`[Role] User ${uid} has role:`, role);

    // Update cache
    roleCache = {
      uid,
      role,
      timestamp: Date.now(),
    };

    return role;
  } catch (error) {
    console.error('Error fetching user role from API:', error);
    return 'participant'; // Default to participant on error
  }
}

/**
 * Check if user has creator or admin access
 */
export function hasCreatorAccess(role: UserRole): boolean {
  return role === 'content_creator' || role === 'admin';
}

/**
 * Get redirect path based on user role
 */
export function getRoleBasedRedirectPath(role: UserRole): string {
  const hasAccess = hasCreatorAccess(role);
  const path = hasAccess ? '/creator' : '/participant';
  console.log(`[Role] Redirecting role "${role}" to ${path} (hasCreatorAccess: ${hasAccess})`);
  return path;
}
